import { addToast, Modal } from '@octano/global-ui';
import { useTranslation } from 'react-i18next';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  AdmissionScope,
  AdmissionViaOption,
  AvailableOffer,
  Offer,
  OfferListFormValues,
} from '../../types';
import { updateAdmissionAction } from '../actions';
import { addOfferAction } from '../managementActions';
import AdmissionProcessOfferForm from './AdmissionProcessOfferForm';

interface Props {
  toggle: () => void;
  offer?: Offer;
  availableOffers: AvailableOffer[];
  admissionVias: AdmissionViaOption[];
  refresh: () => void;
}

export default function OfferModal({
  toggle,
  offer,
  availableOffers,
  admissionVias,
  refresh,
}: Props) {
  const { t } = useTranslation('academic-offer/admission-processes');
  const { id } = useParams<{ id: string }>();
  const isEdit = !!offer;
  const [isSubmitting, setIsSubmitting] = useState(false);

  const onSubmit = useCallback(
    async (
      data: OfferListFormValues & {
        offerId: any;
        admissionViaId?: string | null;
      },
    ) => {
      setIsSubmitting(true);
      const { enrollmentType, quota, offerId, admissionViaId } = data;

      const commonData = {
        admissionScopes: enrollmentType?.value
          ? (enrollmentType.value.toString().split(',') as AdmissionScope[])
          : null,
        quota: quota ? +quota : null,
        admissionViaId: admissionViaId ?? null,
      };

      let result;
      if (isEdit && offer) {
        result = await updateAdmissionAction({
          admissionId: offer.admissionId,
          data: commonData,
        });
      } else {
        if (!id || !offerId) {
          setIsSubmitting(false);
          return;
        }

        result = await addOfferAction({
          id: +id,
          data: {
            offerId: offerId.value,
            ...commonData,
          },
        });
      }

      if (result) {
        addToast({
          text: isEdit
            ? t('processList.modalForm.success')
            : t('offersTable.addSuccess'),
          color: 'success',
        });
      } else {
        addToast({
          text: isEdit
            ? t('processList.modalForm.error')
            : t('offersTable.addError'),
          color: 'danger',
        });
      }
      setIsSubmitting(false);
      refresh();
      toggle();
    },
    [t, isEdit, offer, refresh, toggle, id],
  );

  return (
    <Modal isOpen toggle={toggle} size="lg" closeOnBackdrop={false}>
      <AdmissionProcessOfferForm
        offer={offer}
        availableOffers={availableOffers}
        admissionVias={admissionVias}
        onSubmit={onSubmit}
        onCancel={toggle}
        isSubmitting={isSubmitting}
      />
    </Modal>
  );
}
